/**
 * Localisation for the platform admin console (CLAUDE.md §6).
 *
 * Three rules this module exists to enforce:
 *
 *  1. **No string reaches a screen except through a key.** `en.json` is the
 *     source of truth and is complete; `dv.json` mirrors every key. A locale
 *     test fails the build on drift, because the failure mode of translation is
 *     not a crash, it is a Dhivehi screen with English holes in it that nobody
 *     notices until a Dhivehi speaker opens it.
 *  2. **The locale follows the operator's own choice.** Unlike the tenant app,
 *     the platform realm has no per-user locale: `platform.users` (and the
 *     `User` DTO the admin API returns) carries no `locale` column, so there is
 *     no server preference to adopt. The last explicit choice is mirrored into
 *     localStorage — which is also what makes the login screen come up in the
 *     right language on the next visit — and the browser's `Accept-Language` is
 *     the first guess before any choice has been made.
 *  3. **Direction is part of the locale.** Dhivehi is right-to-left, so
 *     switching locale sets `lang` *and* `dir` on `<html>`; §11 asks for a real
 *     RTL switch, and every logical property in the design system keys off that
 *     one attribute.
 *
 * Composition API only (`legacy: false`) — `$t` is available in templates and
 * `useI18n()` in setup, and the Options-API `this.$t` shim stays out of the
 * bundle.
 */

import { applyDirection, type Direction } from '@erpx/ui'
import { createI18n, type DefaultLocaleMessageSchema } from 'vue-i18n'

import dv from '@/locales/dv.json'
import en from '@/locales/en.json'

export const SUPPORTED_LOCALES = ['en', 'dv'] as const
export type AppLocale = (typeof SUPPORTED_LOCALES)[number]

export const DEFAULT_LOCALE: AppLocale = 'en'

/** Locales written right-to-left. Dhivehi (Thaana) is the only one today. */
const RTL_LOCALES: readonly AppLocale[] = ['dv']

/** Endonyms — a language picker that reads "Dhivehi" to a Dhivehi speaker is
 * a picker they cannot use. Never translated. */
export const LOCALE_NAMES: Readonly<Record<AppLocale, string>> = {
  en: 'English',
  dv: 'ދިވެހި',
}

const STORAGE_KEY = 'erpx.admin.locale'

export type MessageSchema = typeof en

export function isAppLocale(value: unknown): value is AppLocale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value)
}

export function localeDirection(locale: AppLocale): Direction {
  return RTL_LOCALES.includes(locale) ? 'rtl' : 'ltr'
}

function readStoredLocale(): AppLocale | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return isAppLocale(stored) ? stored : null
  } catch {
    // Private mode or a blocked storage partition; fall through to the default.
    return null
  }
}

function persistLocale(locale: AppLocale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale)
  } catch {
    // A remembered preference is a nicety; losing it costs one extra click.
  }
}

/** The browser's preference, used only before an operator has told us theirs. */
function browserLocale(): AppLocale | null {
  if (typeof navigator === 'undefined') return null
  for (const tag of navigator.languages ?? [navigator.language]) {
    const base = tag.split('-')[0]
    if (isAppLocale(base)) return base
  }
  return null
}

/** Locale to boot with: last explicit choice, else the browser's, else `en`. */
export function initialLocale(): AppLocale {
  return readStoredLocale() ?? browserLocale() ?? DEFAULT_LOCALE
}

/**
 * Put the locale on the document. `lang` drives hyphenation, spell-check and
 * the accessibility tree; `dir` drives every logical property in the design
 * system. Both, or neither — a `lang="dv"` document laid out left-to-right is
 * worse than an English one.
 */
export function applyDocumentLocale(locale: AppLocale): void {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('lang', locale)
  }
  applyDirection(localeDirection(locale))
}

/**
 * The catalogue is deliberately typed with vue-i18n's permissive default schema
 * rather than with `typeof en`. A generated union of every key path would reject
 * the handful of *computed* keys this app needs — `common.roles.${role}`,
 * `errors.codes.${code}`, `tenants.provisioning.steps.${step}` — all of which
 * are guarded by `te()` at the call site and fall back to the raw server value.
 * The parity guarantee the schema type would give is what `locales.test.ts`
 * asserts instead, and it asserts it across *both* locales rather than one.
 */
export const i18n = createI18n<DefaultLocaleMessageSchema, AppLocale, false>({
  legacy: false,
  globalInjection: true,
  locale: initialLocale(),
  fallbackLocale: DEFAULT_LOCALE,
  // A key that only exists in `en` is a bug the locale test catches, not
  // something to shout about on every render in development.
  missingWarn: false,
  fallbackWarn: false,
  messages: { en, dv },
})

/** The locale in force right now. */
export function currentLocale(): AppLocale {
  return i18n.global.locale.value
}

/**
 * Switch locale: message catalogue, `<html lang>`, `<html dir>` and the
 * remembered preference, in one call so the four can never disagree.
 */
export function setLocale(locale: AppLocale): void {
  i18n.global.locale.value = locale
  persistLocale(locale)
  applyDocumentLocale(locale)
}

/**
 * Translate outside a component — route titles, toast text built in a store,
 * anything that has no `useI18n()` to reach for.
 */
export const t = i18n.global.t.bind(i18n.global)

/**
 * A message split around one of its own placeholders, so a sentence can wrap a
 * `<kbd>`, a `<strong>` or a `<span class="a-numeric">` around part of itself
 * without ever being assembled from two translated fragments.
 *
 * The placeholder is filled with a marker that cannot occur in prose (`U+0000`)
 * and the result split on it, which means the *translation* decides where the
 * marked-up run sits in the sentence — the thing word-order differences break
 * when fragments are concatenated in template order instead.
 */
const SLOT_MARK = String.fromCharCode(0)

export function tParts(
  key: string,
  slot: string,
  params: Record<string, string | number> = {},
): string[] {
  return t(key, { ...params, [slot]: SLOT_MARK }).split(SLOT_MARK)
}

applyDocumentLocale(currentLocale())
